<template>
  <div
    class="square-terminal-device-code"
    :class="{
      'status-unpaired': deviceStatus === 'UNPAIRED',
      'status-paired': deviceStatus === 'PAIRED',
      'status-expired': deviceStatus === 'EXPIRED',
      'status-unknown': deviceStatus === 'UNKNOWN'
    }"
  >
    <!-- Device Code Header with Name and Status -->
    <div class="device-header">
      <div class="name">
        {{ name }}
      </div>
      <div class="status-indicator">
        <div
          class="status-badge"
          :class="statusClass"
        >
          <font-awesome-icon
            :icon="statusIcon"
            class="status-icon"
            fixed-width
          />
          {{ statusText }}
        </div>
      </div>
    </div>

    <!-- Device Code Information Grid -->
    <div class="device-info">
      <!-- Row 1: Pairing Code/Device ID (spans full width) -->
      <div class="info-section pairing-code full-width">
        <div class="info-label">
          {{ deviceStatus === 'PAIRED' ? 'Device ID' : 'Pairing Code' }}
        </div>
        <div class="info-value pairing-code-value">
          {{ deviceStatus === 'PAIRED' ? deviceId : code }}
        </div>
        <div class="info-detail">
          {{ deviceStatus === 'PAIRED' ? 'Terminal device identifier' : 'Enter this code on device' }}
        </div>
      </div>

      <!-- Row 2: Product Type & Location -->
      <div class="info-section product-type">
        <div class="info-label">
          Product Type
        </div>
        <div class="info-value">
          {{ productType }}
        </div>
        <div class="info-detail">
          Terminal API
        </div>
      </div>

      <div
        v-if="deviceLocation"
        class="info-section location-info"
      >
        <div class="info-label">
          Location
        </div>
        <div class="info-value">
          {{ deviceLocation.display_name || deviceLocation.name }}
        </div>
        <div class="info-detail">
          {{ deviceLocation.address && deviceLocation.address.locality || 'Unknown' }}
        </div>
      </div>

      <!-- Row 3: Expiry & Created Date -->
      <div class="info-section pairing-expiry">
        <div class="info-label">
          Expires
        </div>
        <div class="info-value">
          <font-awesome-icon
            :icon="expiryIcon"
            :class="expiryClass"
            fixed-width
          />
          {{ formattedPairBy }}
        </div>
        <div class="info-detail">
          {{ timeUntilExpiry }}
        </div>
      </div>

      <div class="info-section created-date">
        <div class="info-label">
          Created
        </div>
        <div class="info-value">
          {{ formattedCreatedAt }}
        </div>
        <div class="info-detail">
          {{ timeAgoCreated }}
        </div>
      </div>

      <!-- Status Changed (hidden on mobile, shown on larger screens) -->
      <div class="info-section status-changed desktop-only">
        <div class="info-label">
          Status Updated
        </div>
        <div class="info-value">
          {{ formattedStatusChanged }}
        </div>
        <div class="info-detail">
          {{ timeAgoStatusChanged }}
        </div>
      </div>
    </div>

    <!-- Remove Device Code Button -->
    <div
      v-if="remove"
      class="remove-device-button"
    >
      <b-modal
        :id="`confirm-dialog--${device.id}`"
        :title="`Remove Device Code: ${name}`"
        static
        hide-header-close
        @ok="removeTerminalDeviceCode"
      >
        <template #modal-header="{ close }">
          <div class="modal-header-custom">
            <h4 class="modal-title">
              Remove Device Code: {{ name }}
            </h4>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="close-button"
              :disabled="processing"
              @click="close()"
            >
              <font-awesome-icon icon="times" />
            </b-button>
          </div>
        </template>

        <div class="device-removal-details">
          <p><strong>Are you sure that you wish to remove this terminal device code?</strong></p>

          <div class="device-details-summary">
            <h6>Device Code Details:</h6>
            <div class="detail-row">
              <span class="detail-label">Name:</span>
              <span class="detail-value">{{ name }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Pairing Code:</span>
              <span class="detail-value pairing-code">{{ code }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Status:</span>
              <span class="detail-value">
                <span
                  class="status-badge-small"
                  :class="statusClass"
                >
                  <font-awesome-icon
                    :icon="statusIcon"
                    fixed-width
                  />
                  {{ statusText }}
                </span>
              </span>
            </div>
            <div
              v-if="deviceLocation"
              class="detail-row"
            >
              <span class="detail-label">Location:</span>
              <span class="detail-value">{{ deviceLocation.display_name || deviceLocation.name }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Created:</span>
              <span class="detail-value">{{ formattedCreatedAt }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Expires:</span>
              <span class="detail-value">
                <font-awesome-icon
                  :icon="expiryIcon"
                  :class="expiryClass"
                  fixed-width
                />
                {{ formattedPairBy }}
              </span>
            </div>
          </div>

          <div class="warning-message">
            <font-awesome-icon
              icon="triangle-exclamation"
              class="warning-icon"
            />
            <span>This action cannot be undone and will prevent the device from pairing.</span>
          </div>
        </div>

        <template #modal-footer="{ cancel }">
          <div class="modal-footer-custom">
            <b-button
              variant="secondary"
              :disabled="processing"
              @click="cancel()"
            >
              Cancel
            </b-button>
            <b-button
              variant="danger"
              :disabled="processing"
              :data-cy="'confirm-remove-device-code-' + device.id"
              @click="removeTerminalDeviceCode"
            >
              <b-spinner
                v-if="processing"
                small
                class="me-2"
              />
              <font-awesome-icon
                v-if="!processing"
                icon="trash"
                class="me-1"
              />
              Remove
            </b-button>
          </div>
        </template>
      </b-modal>
      <b-button
        variant="outline-danger"
        size="sm"
        :disabled="processing"
        :data-cy="'remove-device-code-' + device.id"
        @click="$bvModal.show(`confirm-dialog--${device.id}`)"
      >
        <font-awesome-icon icon="trash" />
        Remove
        <b-spinner
          v-if="processing"
          small
        />
      </b-button>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

export default {
  name: 'square-terminal-device-code-details',
  props: {
    device: {
      type: Object,
      required: true
    },
    remove: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function() {
    return {
      processing: false
    }
  },
  computed: {
    name: function() {
      return this.device.name || 'Unknown Device Code'
    },

    code: function() {
      return this.device.code || 'N/A'
    },

    productType: function() {
      return this.device.product_type || 'TERMINAL_API'
    },

    // Device ID (actual terminal device ID when paired)
    deviceId: function() {
      // When paired, use the actual device ID from the device object
      // When unpaired, fall back to the device code ID
      if (this.deviceStatus === 'PAIRED' && this.device.device && this.device.device.device_id) {
        return this.device.device.device_id
      }
      return this.device.device_id || 'N/A'
    },

    // Device Status
    deviceStatus: function() {
      return this.device.status || 'UNKNOWN'
    },

    statusClass: function() {
      switch (this.deviceStatus) {
      case 'UNPAIRED': return 'status-pending'
      case 'PAIRED': return 'status-online'
      case 'EXPIRED': return 'status-offline'
      default: return 'status-unknown'
      }
    },

    statusIcon: function() {
      switch (this.deviceStatus) {
      case 'UNPAIRED': return 'circle-question'
      case 'PAIRED': return 'circle-check'
      case 'EXPIRED': return 'circle-xmark'
      default: return 'circle-question'
      }
    },

    statusText: function() {
      switch (this.deviceStatus) {
      case 'UNPAIRED': return 'Awaiting Pairing'
      case 'PAIRED': return 'Paired'
      case 'EXPIRED': return 'Expired'
      default: return 'Unknown'
      }
    },

    // Location Information
    deviceLocation: function() {
      const locationId = this.device.location_id
      if (locationId) {
        return this.$store.state.square.locations && this.$store.state.square.locations.find(loc => loc.id === locationId)
      }
      return null
    },

    // Date formatting
    formattedPairBy: function() {
      if (!this.device.pair_by) return 'N/A'
      return dayjs(this.device.pair_by).format('MMM D, YYYY HH:mm')
    },

    formattedCreatedAt: function() {
      if (!this.device.created_at) return 'N/A'
      return dayjs(this.device.created_at).format('MMM D, YYYY HH:mm')
    },

    formattedStatusChanged: function() {
      if (!this.device.status_changed_at) return 'N/A'
      return dayjs(this.device.status_changed_at).format('MMM D, YYYY HH:mm')
    },

    // Relative time
    timeUntilExpiry: function() {
      if (!this.device.pair_by) return 'Unknown'
      const now = dayjs()
      const expiry = dayjs(this.device.pair_by)

      if (expiry.isBefore(now)) {
        return 'Expired'
      }
      return `in ${expiry.from(now, true)}`
    },

    timeAgoCreated: function() {
      if (!this.device.created_at) return 'Unknown'
      return dayjs(this.device.created_at).fromNow()
    },

    timeAgoStatusChanged: function() {
      if (!this.device.status_changed_at) return 'Unknown'
      return dayjs(this.device.status_changed_at).fromNow()
    },

    // Expiry status
    isExpired: function() {
      if (!this.device.pair_by) return false
      return dayjs().isAfter(dayjs(this.device.pair_by))
    },

    isExpiringSoon: function() {
      if (!this.device.pair_by) return false
      const expiry = dayjs(this.device.pair_by)
      const now = dayjs()
      const hoursUntilExpiry = expiry.diff(now, 'hours')
      return hoursUntilExpiry <= 2 && hoursUntilExpiry > 0
    },

    expiryIcon: function() {
      if (this.isExpired) return 'circle-xmark'
      if (this.isExpiringSoon) return 'triangle-exclamation'
      return 'clock'
    },

    expiryClass: function() {
      if (this.isExpired) return 'expiry-expired'
      if (this.isExpiringSoon) return 'expiry-warning'
      return 'expiry-normal'
    }
  },
  methods: {
    removeTerminalDeviceCode: function() {
      this.processing = true
      console.log('Starting device code removal for:', this.device.id)

      // Create a copy of device data before removal
      const deviceToRemove = { ...this.device }

      this.$store.dispatch('square/removeDeviceCode', { deviceCode: this.device.id })
        .then(_ => {
          // Emit the removed event BEFORE reloading the list
          // This ensures the success message is set before the store state changes
          this.$emit('removed', deviceToRemove)

          // Then reload the list
          return this.$store.dispatch('square/loadDeviceCodesList', { force: true })
        })
        .then(result => {
          console.log('Device code removal successful, closing modal')

          // Close the modal after everything is complete
          this.$bvModal.hide(`confirm-dialog--${this.device.id}`)
        })
        .catch(err => {
          console.error('Device code removal failed:', err)
          this.$emit('error', { device: this.device, error: err })
        })
        .finally(_ => {
          this.processing = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
  .square-terminal-device-code {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;

    // Dynamic background colors based on device status
    &.status-unpaired {
      background-color: rgba(255, 243, 205, 0.3); // More visible transparent version of pending badge color
    }

    &.status-paired {
      background-color: rgba(212, 237, 218, 0.3); // More visible transparent version of online badge color
    }

    &.status-expired {
      background-color: rgba(248, 215, 218, 0.3); // More visible transparent version of offline badge color
    }

    &.status-unknown {
      background-color: #ffffff; // Keep white for unknown status
    }

    .device-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 16px;

      .name {
        font-weight: bold;
        font-size: 1.2rem;
        color: #333;
        flex: 1;
        line-height: 1.3;
      }

      .status-indicator {
        flex-shrink: 0;
        margin-left: 12px;
      }
    }

    .status-badge {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;

      &.status-online {
        background-color: #d4edda;
        color: #155724;
        border: 1px solid #c3e6cb;
      }

      &.status-offline {
        background-color: #f8d7da;
        color: #721c24;
        border: 1px solid #f5c6cb;
      }

      &.status-pending {
        background-color: #fff3cd;
        color: #856404;
        border: 1px solid #ffeaa7;
      }

      &.status-unknown {
        background-color: #f8f9fa;
        color: #6c757d;
        border: 1px solid #dee2e6;
      }
    }

    .device-info {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto auto auto;
      gap: 16px 12px;
      flex: 1;
      margin-bottom: 16px;

      .info-section {
        display: flex;
        flex-direction: column;
        min-height: 60px;

        &.full-width {
          grid-column: 1 / -1; // Spans full width
        }

        &.desktop-only {
          display: none; // Hidden by default, shown on larger screens
        }

        .info-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }

        .info-value {
          font-weight: 600;
          color: #333;
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 2px;

          &.pairing-code-value {
            font-family: 'Courier New', monospace;
            font-size: 1.1rem;
            letter-spacing: 2px;
            color: #007bff;
            background-color: #f8f9fa;
            padding: 8px 12px;
            border-radius: 6px;
            border: 1px solid #e9ecef;
          }
        }

        .info-detail {
          font-size: 0.8rem;
          color: #6c757d;
          line-height: 1.2;
        }
      }
    }

    // Expiry status colors
    .expiry-normal {
      color: #28a745;
    }

    .expiry-warning {
      color: #ffc107;
    }

    .expiry-expired {
      color: #dc3545;
    }

    .remove-device-button {
      display: flex;
      justify-content: flex-end;
      margin-top: auto;
      padding-top: 12px;
      border-top: 1px solid #e9ecef;

      .btn {
        display: flex;
        align-items: center;
        gap: 6px;
      }
    }

    // Responsive adjustments
    @media (max-width: 576px) {
      .device-info {
        grid-template-columns: 1fr;
        gap: 12px;

        .info-section {
          &.full-width {
            grid-column: 1; // Reset to single column on mobile
          }

          &.desktop-only {
            display: flex; // Show status changed on mobile in single column
          }
        }
      }

      .device-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;

        .status-indicator {
          margin-left: 0;
        }
      }
    }

    // Show desktop-only sections on larger screens
    @media (min-width: 768px) {
      .device-info {
        .info-section {
          &.desktop-only {
            display: flex;
            grid-column: 1 / -1; // Spans full width on desktop
          }
        }
      }
    }

    // Modal styles for device removal confirmation
    .device-removal-details {
      .device-details-summary {
        background-color: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        padding: 1rem;
        margin: 1rem 0;

        h6 {
          color: #495057;
          margin-bottom: 0.75rem;
          font-weight: 600;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.25rem 0;
          border-bottom: 1px solid #e9ecef;

          &:last-child {
            border-bottom: none;
          }

          .detail-label {
            font-weight: 600;
            color: #6c757d;
            font-size: 0.875rem;
          }

          .detail-value {
            color: #495057;
            font-size: 0.875rem;

            &.pairing-code {
              font-family: 'Courier New', monospace;
              background-color: #e9ecef;
              padding: 0.25rem 0.5rem;
              border-radius: 4px;
              font-weight: 600;
              color: #007bff;
            }
          }

          .status-badge-small {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 2px 6px;
            border-radius: 8px;
            font-size: 0.65rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;

            &.status-online {
              background-color: #d4edda;
              color: #155724;
              border: 1px solid #c3e6cb;
            }

            &.status-offline {
              background-color: #f8d7da;
              color: #721c24;
              border: 1px solid #f5c6cb;
            }

            &.status-pending {
              background-color: #fff3cd;
              color: #856404;
              border: 1px solid #ffeaa7;
            }

            &.status-unknown {
              background-color: #f8f9fa;
              color: #6c757d;
              border: 1px solid #dee2e6;
            }
          }
        }
      }

      .warning-message {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem;
        background-color: #fff3cd;
        border: 1px solid #ffeaa7;
        border-radius: 6px;
        margin-top: 1rem;
        color: #856404;
        font-size: 0.875rem;

        .warning-icon {
          color: #ffc107;
          flex-shrink: 0;
        }
      }
    }

    // Modal header styling to match create device code modal
    .modal-header-custom {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding: 1rem 1.5rem;
      border-bottom: 1px solid #dee2e6;
      margin: 0;

      .modal-title {
        margin: 0;
        font-size: 1.25rem;
        color: #333;
        flex: 1;
      }

      .close-button {
        padding: 0.375rem 0.75rem;
        border-radius: 0.25rem;
        transition: background-color 0.2s;
        margin-left: auto;
        flex-shrink: 0;

        &:hover {
          background-color: #f8f9fa;
        }
      }
    }

    .modal-footer-custom {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.5rem;
      border-top: 1px solid #dee2e6;
      margin: 0;

      .btn {
        min-width: 120px;
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
      }
    }
  }
</style>
